import { RootState } from "@/store";
import { updateOnboardingProgress } from "@/store/userSlice";
import { EmployeeState } from "@/types/employeeState";
import {
  ENROLLED_STATUS_TO_NEXT_STEP,
  STEP_TO_ENROLLED_STATUS,
  WIZARD_STATUS_ORDER,
} from "@/utils/wizardProgress";
import { useDispatch, useSelector } from "react-redux";

interface UseWizardProgressProps {
  saveProgress: (stepNumber: number) => void;
  getStartingStep: (isModal?: boolean) => number;
  saveTermsAcceptance: () => void;
  isTermsAccepted: () => boolean;
  resetProgress: () => void;
  progress: {
    lastCompletedStep: number;
    terms_accepted: boolean;
  };
}

export const useWizardProgress = (): UseWizardProgressProps => {
  const dispatch = useDispatch();
  const onboardingProgress = useSelector(
    (state: RootState) => state.user.onboardingProgress,
  );

  const saveProgress = (stepNumber: number) => {
    const newStatus = STEP_TO_ENROLLED_STATUS[stepNumber];
    if (newStatus) {
      dispatch(updateOnboardingProgress(newStatus));
    }
  };

  const getStartingStep = (isModal: boolean = false): number => {
    const nextStep =
      ENROLLED_STATUS_TO_NEXT_STEP[onboardingProgress as EmployeeState] ?? 1;
    const clamped = Math.min(nextStep, 6);
    return isModal ? Math.max(2, clamped) : clamped;
  };

  const saveTermsAcceptance = () => {
    dispatch(updateOnboardingProgress(EmployeeState.TermsAccepted));
  };

  const isTermsAccepted = (): boolean => {
    return WIZARD_STATUS_ORDER.includes(onboardingProgress as EmployeeState);
  };

  const resetProgress = (): void => {
    const currentIndex = WIZARD_STATUS_ORDER.indexOf(
      onboardingProgress as EmployeeState,
    );
    const beneficiaryIndex = WIZARD_STATUS_ORDER.indexOf(
      EmployeeState.BeneficiaryAdded,
    );
    if (currentIndex >= beneficiaryIndex) {
      dispatch(updateOnboardingProgress(EmployeeState.TermsAccepted));
    }
  };

  const terms_accepted = WIZARD_STATUS_ORDER.includes(
    onboardingProgress as EmployeeState,
  );
  const lastCompletedStep =
    (ENROLLED_STATUS_TO_NEXT_STEP[onboardingProgress as EmployeeState] ?? 1) -
    1;

  return {
    saveProgress,
    getStartingStep,
    saveTermsAcceptance,
    isTermsAccepted,
    resetProgress,
    progress: { lastCompletedStep, terms_accepted },
  };
};
